/* eslint-disable camelcase */
import { useCallback, useEffect, useState } from 'react'
import { ApolloError, useLazyQuery } from '@apollo/client'
import moment from 'moment-timezone'
import { graphql } from '@/generated/gql/gql'
import {
  GetMyMenuQuery,
  Meal,
  UseCanteenMenuState_UserDataFragmentFragment,
} from '@/generated/gql/graphql'
import { FragmentType, useFragment } from '@/generated/gql'
import { useInterval } from 'react-use'

const useCcMenuUserDataFragment = graphql(`
  fragment UseCanteenMenuState_UserDataFragment on User {
    upcomingCcMenus {
      date
      breakfast {
        menuIsPublished
      }
      lunch {
        menuIsPublished
      }
      dinner {
        menuIsPublished
      }
    }
  }
`)

const useCcMenuCurrentMenuQuery = graphql(`
  query getMyMenu($meal: Meal!, $date: DateTime!) {
    me {
      id
      ccMenu(input: { meal: $meal, date: $date }) {
        id
        mealServiceData {
          ...CanteenMenu_CurrentMenuDataFragment
          id
          meal
          menuIsPublished
          openTime
          closeTime
          availableSubsidy
          timeslots {
            id
            vendorMenus {
              id
              vendor {
                currency
              }
            }
            id
            startTime
            endTime
            ordersOpenTime
            ordersCloseTime
          }
        }
      }
    }
  }
`)

export enum CCMenuStateCustomErrorCode {
  NoData = 'NO_MENU_DATA',
}

type CCMenuStateCustomError = {
  message: string
  name: CCMenuStateCustomErrorCode
}

type Props = {
  paramsDate: string
  paramsMeal: string
  paramsTimeslot: string
  userData?: FragmentType<typeof useCcMenuUserDataFragment> | null
}

export const useCcMenuState = ({
  paramsDate,
  paramsMeal,
  paramsTimeslot,
  userData: userDataProp,
}: Props) => {
  const userData = useFragment(useCcMenuUserDataFragment, userDataProp)
  const [loading, setLoading] = useState(true)
  const [error] = useState<ApolloError | CCMenuStateCustomError | null>(null)
  const [validDate, setValidDate] = useState<string | null>(null)
  const [validMeal, setValidMeal] = useState<string | null>(null)
  const [validTimeslot, setValidTimeslot] = useState<string | null>(null)
  const [orderTimeRemaining, setOrderTimeRemaining] = useState(moment.duration())
  const [menuClosed, setMenuClosed] = useState(false)

  const [timeslotHour, timeslotMinute] = paramsTimeslot.split(':')
  const paramsTimeslotDate = moment(paramsDate)
    .set('hour', parseInt(timeslotHour, 10))
    .set('minute', parseInt(timeslotMinute, 10))
  const paramsTimeslotISOString = paramsTimeslotDate.toISOString()

  const [
    runGetMenuDataQuery,
    {
      data: menuData,
      error: graphqlError,
      previousData: previousMenuData,
      refetch,
      loading: loadingMenuData,
      called: menuDataCalled,
    },
  ] = useLazyQuery(useCcMenuCurrentMenuQuery)

  const getValidDateAndMeal = useCallback(
    (
      dateToCheck: string,
      mealToCheck: string,
      userData: UseCanteenMenuState_UserDataFragmentFragment,
    ) => {
      let dateToUse = moment().format('YYYY-MM-DD')
      const meals = ['breakfast', 'lunch', 'dinner']
      let mealToUse = 'lunch'
      const foundDate = userData?.upcomingCcMenus.find(
        (menu) => moment(menu.date).format('YYYY-MM-DD') === dateToCheck,
      )
      const firstServiceWithMenu = userData?.upcomingCcMenus.find(
        (menu) =>
          menu.breakfast?.menuIsPublished ||
          menu.lunch?.menuIsPublished ||
          menu.dinner?.menuIsPublished,
      )
      if (foundDate) {
        dateToUse = dateToCheck || dateToUse
        mealToUse = mealToCheck && foundDate[mealToCheck as Meal] ? mealToCheck : mealToUse
      } else if (firstServiceWithMenu) {
        const nextMenu = firstServiceWithMenu
        dateToUse = moment(nextMenu.date).format('YYYY-MM-DD')
        mealToUse =
          meals.find((mealPossibility) => nextMenu[mealPossibility as Meal]?.menuIsPublished) ||
          mealToUse
      }

      return { date: dateToUse, meal: mealToUse as Meal }
    },
    [],
  )

  const getValidTimeslot = useCallback(
    (
      timeslotToCheck: string,
      menuTimeslots: NonNullable<GetMyMenuQuery['me']['ccMenu']>['mealServiceData']['timeslots'],
    ) => {
      const firstAvailableTimeslot = menuTimeslots.find((timeslot) =>
        moment().isBefore(moment(timeslot.ordersCloseTime)),
      )
      const matchingTimeslot = menuTimeslots.find((timeslot) =>
        moment(timeslot.startTime).isSame(moment(timeslotToCheck)),
      )
      const validTimeslot = matchingTimeslot
        ? matchingTimeslot.startTime
        : firstAvailableTimeslot?.startTime || menuTimeslots?.[0]?.startTime
      return validTimeslot
    },
    [],
  )

  const calculateTimeLeft = useCallback((closeTime: string) => {
    const cutoffTime = moment(closeTime || new Date())
    const timeRemaining = moment.duration(cutoffTime.diff(moment()))
    return timeRemaining
  }, [])

  useEffect(() => {
    if (!userData) return
    const { meal, date } = getValidDateAndMeal(paramsDate, paramsMeal, userData)
    setValidDate(date)
    setValidMeal(meal)
    const dateAsISOString = moment(date).toISOString()
    runGetMenuDataQuery({
      variables: { date: dateAsISOString, meal },
      fetchPolicy: 'cache-and-network',
    })
  }, [runGetMenuDataQuery, paramsDate, paramsMeal, getValidDateAndMeal, userData])

  useEffect(() => {
    if (!menuDataCalled || (!menuData && loadingMenuData)) return
    if (!menuData?.me.ccMenu?.mealServiceData.timeslots) return
    const validTimeslot = getValidTimeslot(
      paramsTimeslotISOString,
      menuData?.me.ccMenu?.mealServiceData.timeslots,
    )
    setValidTimeslot(validTimeslot ?? null)
    setLoading(false)
  }, [menuData, paramsTimeslotISOString, getValidTimeslot, loadingMenuData, menuDataCalled])

  useInterval(() => {
    if (!menuData?.me.ccMenu) return
    const timeLeft = calculateTimeLeft(menuData.me.ccMenu.mealServiceData.closeTime)
    const menuIsNowClosed = timeLeft.milliseconds() < 0
    setOrderTimeRemaining(timeLeft)
    if (menuIsNowClosed) {
      setMenuClosed(true)
    } else {
      setMenuClosed(false)
    }
  }, 3000)

  const updateTimeslot = (timeslot: string) => {
    if (!menuData?.me.ccMenu?.mealServiceData.timeslots) return
    const validTimeslot = getValidTimeslot(timeslot, menuData.me.ccMenu.mealServiceData.timeslots)
    if (!validTimeslot) return
    setValidTimeslot(validTimeslot)
  }

  const updateSelectedService = (updateValues: Partial<{ date: string; meal: string }>) => {
    const dateToCheck = updateValues.date || validDate
    const mealToCheck = updateValues.meal || validMeal
    if (!dateToCheck || !mealToCheck || !userData) return
    setLoading(true)
    const { meal, date } = getValidDateAndMeal(dateToCheck, mealToCheck, userData)
    setValidMeal(meal)
    setValidDate(date)
    setValidTimeslot(null)
  }

  return {
    loading: loading || loadingMenuData || !menuDataCalled,
    called: menuDataCalled,
    date: validDate,
    meal: validMeal,
    timeslot: validTimeslot,
    error: error ?? graphqlError,
    previousMenuData: previousMenuData?.me.ccMenu?.mealServiceData,
    menuData: menuData?.me.ccMenu?.mealServiceData,
    orderTimeRemaining,
    menuClosed,
    refetch,
    updateSelectedService,
    updateTimeslot,
  }
}
